import { Component, HostListener, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AppDialogComponent } from 'src/app/components/app-dialog/app-dialog.component';
import { AuthService } from 'src/app/services/auth.service';
import {
	UntypedFormControl,
	UntypedFormGroup,
	Validators,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { NavigationHistoryService } from 'src/app/services/navigation-history.service';
import { MenuItemType } from 'src/app/nav/nav.component';
import { UserProfileService } from 'src/app/services/user-profile.service';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { LoginContainerComponent } from '../login-container/login-container.component';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { LoadingIndicatorComponent } from '../../../components/loading-indicator/loading-indicator.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';

@Component({
	selector: 'app-login',
	templateUrl: './login-authentication.component.html',
	styleUrls: [
		'../login.component.scss',
		'../../common/modal.scss',
		'../../common/main.scss',
	],
	imports: [
		LoginContainerComponent,
		FormsModule,
		ReactiveFormsModule,
		MatFormField,
		MatLabel,
		MatInput,
		MatError,
		MatCheckbox,
		LoadingIndicatorComponent,
		MatButton,
		MatIcon,
		MatMenuTrigger,
		MatMenu,
		MatMenuItem,
	],
})
export class LoginAuthenticationComponent implements OnInit {
	hide = true;
	readonly formGroup: UntypedFormGroup = new UntypedFormGroup({
		email: new UntypedFormControl('', [Validators.required, Validators.email]),
		password: new UntypedFormControl('', [Validators.required]),
	});

	allDayCheckbox = false;
	logoutMessage?: string;
	siteLanguage = 'English';
	siteLocale!: string;
	languageList = [
		{ code: 'en-GB', label: 'English (UK)' },
		{ code: 'en-US', label: 'English (US)' },
		/*{ code: 'en-SA', label: 'English (UAE)' },
    { code: 'en-ZA', label: 'English (South Africa)' },
    { code: 'ar-SA', label: 'عربالعربية (المملكة العربية السعودية)' },
    { code: 'ar-AE', label: 'العربية (الإمارات العربية المتحدة)' },
    { code: 'bn', label: 'বাংলা' },
    { code: 'bg', label: 'български' },
    { code: 'cy', label: 'Cymraeg' },
    { code: 'cs', label: 'čeština' },*/
		{ code: 'da', label: 'Dansk' },
		{ code: 'de', label: 'Deutsch' },
		// { code: 'de-AT', label: 'Deutsch (Österreich)' },
		// { code: 'de-BE', label: 'Deutsch (Belgien)' },
		// { code: 'de-LU', label: 'Deutsch (Luxemburg)' },
		// { code: 'de-CH', label: 'Deutsch (Schweiz)' },
		/*{ code: 'et', label: 'eesti keel' },
    { code: 'el', label: 'Ελληνικά' },
    { code: 'el-CY', label: 'Ελληνικά (Κύπρος)' },*/
		{ code: 'es', label: 'Español' },
		/*{ code: 'fil', label: 'Filipino' },*/
		{ code: 'fr', label: 'Français' },
		// { code: 'fr-BE', label: 'Français (Belgique)' },
		// { code: 'fr-LU', label: 'Français (Luxembourg)' },
		// { code: 'fr-CH', label: 'Français (Suisse)' },
		// { code: 'ga-IE', label: 'Gaeilge' },
		// { code: 'gu', label: 'ગુજરાતી' },
		// { code: 'hi', label: 'हिंदी' },
		// { code: 'hr', label: 'Hrvatski' },
		{ code: 'it', label: 'Italiano' },
		// { code: 'it-CH', label: 'Italiano (Svizzera)' },
		// { code: 'lv-LV', label: 'latviski' },
		// { code: 'lt', label: 'lietuvių' },
		// { code: 'hu-HU', label: 'Magyar' },
		// { code: 'mt', label: 'Malti' },
		// { code: 'nl-BE', label: 'Nederlands (België)' },
		{ code: 'nl-NL', label: 'Nederlands' },
		/*{ code: 'pl', label: 'Polskie' },
    { code: 'pt', label: 'Portugues' },
    { code: 'ro-RO', label: 'Română' },
    { code: 'fi', label: 'Suomalainen' },
    { code: 'sl', label: 'Slovenščina' },
    { code: 'sk', label: 'Slovenský' },*/
		{ code: 'sv', label: 'Svenska' },
		// { code: 'sv-FI', label: 'Svenska (Finland)' },
		/*{ code: 'pa', label: 'ਪੰਜਾਬੀ' },*/
		{ code: 'tr', label: 'Türk' } /*,
    { code: 'ur', label: 'اردو' }*/,
	];

	constructor(
		private dialog: MatDialog,
		private authService: AuthService,
		private router: Router,
		private readonly navHistory: NavigationHistoryService,
		private readonly profileService: UserProfileService,
		private readonly loadingIndicatorService: LoadingIndicatorService
	) {
		this.logoutMessage =
			this.router.lastSuccessfulNavigation?.extras.state?.message;
	}

	ngOnInit(): void {
		if (this.logoutMessage) {
			const data = { title: 'Logout', message: this.logoutMessage };
			this.dialog.open(AppDialogComponent, { data });
		}
		this.siteLocale = window.location.pathname.split('/')[1];
		this.siteLanguage =
			this.languageList.find((f) => f.code === this.siteLocale)?.label ??
			this.languageList[0].label;
		this.navHistory.clearHistory(0);
		this.navHistory.setLastNavigationItemClicked(MenuItemType.home);
		this.profileService.clearUserProfile();
	}

	@HostListener('window:keydown', ['$event'])
	// tslint:disable-next-line: typedef
	async keyEvent(event: KeyboardEvent) {
		if (event.key === 'Enter') {
			await this.onLoginClick();
		}
	}

	navigateToForgotPassword(): void {
		this.router.navigateByUrl('/forgot-password');
	}

	updateAllDayCheckbox(): void {
		this.allDayCheckbox = !this.allDayCheckbox;
	}

	async onLoginClick(): Promise<void> {
		Object.keys(this.formGroup.controls).forEach((it: string) =>
			this.formGroup.get(it)?.markAsTouched({ onlySelf: true })
		);

		if (!this.formGroup.valid) {
			return;
		}

		const email = this.formGroup.get('email')?.value;
		const password = this.formGroup.get('password')?.value;

		await this.performLogin(email, password);
		await this.router.navigateByUrl('/');
	}

	showError(field: string): boolean {
		const control = this.formGroup.get(field);
		return !(control?.valid ?? true) && (control?.touched ?? false);
	}

	errorMessageHandler(): boolean {
		return true;
	}

	private async performLogin(email: string, password: string): Promise<void> {
		return this.authService
			.login(email, password, this.allDayCheckbox)
			.catch((err) => {
				const data = {
					title: $localize`:@@loginAuthentication-errorTitle:Login error`,
					message: $localize`:@@loginAuthentication-errorMessage:Please check the credentials entered and retry.`,
				};
				this.dialog.open(AppDialogComponent, { data });
			});
	}
}
